import React, { useEffect, useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { RequestsView } from "./RequestsView";
import { Kanban, List, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Request, RequestType } from "@/types/request";
import { RequestFilters } from "@/components/pages/requests/components/RequestFilters";
import CreateRequestModal from "@/components/pages/requests/components/modals/CreateRequestModal";
import EditRequestModal from "@/components/pages/requests/components/modals/EditRequestModal";
import { useSorting } from "@/hooks/useSorting";
import {
  useRequests,
  useUpdateRequest,
} from "@/components/pages/requests/hooks/useRequests";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { DateRange } from "@/hooks/useDateRange";
import { useHotel } from "@/hooks/useHotel";
import { useCheckboxSelection } from "@/hooks/useCheckboxSelection";
import { RequestBulkActions } from "./components/RequestBulkActions";
import { useRoomCategories } from "@/hooks/useRoomCategories";
import RequestStats from "./components/RequestStats";

export enum ViewModes {
  LIST = "LIST",
  KANBAN = "Kanban",
}

const LIST_PER_PAGE = 10;
const KANBAN_PER_PAGE = 200;
const ROOM_CATEGORIES_LOOKUP_LIMIT = 500;

const today = new Date();
today.setHours(0, 0, 0, 0);

const futureDate = new Date();
futureDate.setDate(today.getDate() + 100);
futureDate.setHours(0, 0, 0, 0);

const defaultDateRange: DateRange = {
  from: today,
  to: futureDate,
};

const ViewModeIcon = {
  [ViewModes.LIST]: (className: string) => <List className={className} />,
  [ViewModes.KANBAN]: (className: string) => <Kanban className={className} />,
};

const REQUEST_SEARCH_PARAM_KEYS = {
  type: "type",
  view: "view",
  booking: "booking",
  status: "status",
  service: "service",
  assignedStaff: "assignedStaff",
  createdBy: "createdBy",
  priority: "priority",
  startDate: "startDate",
  endDate: "endDate",
} as const;

const formatDateSearchParam = (date: Date | undefined): string | null => {
  if (!date) {
    return null;
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const parseDateSearchParam = (value: string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return undefined;
  }

  return new Date(year, month - 1, day);
};

const isSameDate = (left?: Date, right?: Date): boolean => {
  if (!left && !right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return formatDateSearchParam(left) === formatDateSearchParam(right);
};

const setMultiValueSearchParam = (
  params: URLSearchParams,
  key: string,
  values: string[],
) => {
  params.delete(key);

  values.filter(Boolean).forEach((value) => {
    params.append(key, value);
  });
};

const getRequestTypeFromSearchParam = (value: string | null): RequestType => {
  if (Object.values(RequestType).includes(value as RequestType)) {
    return value as RequestType;
  }

  return RequestType.GUEST;
};

const getViewModeFromSearchParam = (value: string | null): ViewModes => {
  if (Object.values(ViewModes).includes(value as ViewModes)) {
    return value as ViewModes;
  }

  return ViewModes.LIST;
};

export const RequestsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getRequestTypeFromSearchParam(
    searchParams.get(REQUEST_SEARCH_PARAM_KEYS.type),
  );
  const viewMode = getViewModeFromSearchParam(
    searchParams.get(REQUEST_SEARCH_PARAM_KEYS.view),
  );
  const statusFilter = searchParams.getAll(REQUEST_SEARCH_PARAM_KEYS.status);
  const createdByFilter = searchParams.getAll(
    REQUEST_SEARCH_PARAM_KEYS.createdBy,
  );
  const assignedStaffFilter = searchParams.getAll(
    REQUEST_SEARCH_PARAM_KEYS.assignedStaff,
  );
  const serviceFilter = searchParams.getAll(REQUEST_SEARCH_PARAM_KEYS.service);
  const priorityFilter =
    searchParams.get(REQUEST_SEARCH_PARAM_KEYS.priority) === "true";
  const searchParamsKey = searchParams.toString();
  const dateRangeFilter = useMemo(() => {
    const hasStartDate = searchParams.has(REQUEST_SEARCH_PARAM_KEYS.startDate);
    const hasEndDate = searchParams.has(REQUEST_SEARCH_PARAM_KEYS.endDate);

    if (!hasStartDate && !hasEndDate) {
      return defaultDateRange;
    }

    return {
      from: parseDateSearchParam(
        searchParams.get(REQUEST_SEARCH_PARAM_KEYS.startDate),
      ),
      to: parseDateSearchParam(
        searchParams.get(REQUEST_SEARCH_PARAM_KEYS.endDate),
      ),
    };
  }, [searchParams]);

  const getRequestTabLabel = (type: RequestType) => {
    switch (type) {
      case RequestType.GUEST:
        return t("requests.page.guestRequests");
      case RequestType.INTERNAL:
        return t("requests.page.internalRequests");
      case RequestType.ROOM:
        return t("requests.page.roomService");
      default:
        return type;
    }
  };

  const getViewModeLabel = (mode: ViewModes) => {
    if (mode === ViewModes.LIST) {
      return t("requests.page.list");
    }
    return mode;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const {
    sortField,
    sortDirection,
    handleSort: originalHandleSort,
  } = useSorting();
  const handleSort = (field: string) => {
    setCurrentPage(1);
    originalHandleSort(field);
  };

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);

  const updateRequestMutation = useUpdateRequest();

  const { hotelId } = useHotel();
  const { data: roomCategoriesResponse } = useRoomCategories(
    hotelId ?? "",
    1,
    ROOM_CATEGORIES_LOOKUP_LIMIT,
    undefined,
    !!hotelId,
  );
  const roomCategoryById = useMemo(
    () =>
      Object.fromEntries(
        (roomCategoriesResponse?.data ?? []).map((category) => [
          category.id,
          { name: category.name, pmsType: category.pmsType },
        ]),
      ) as Record<string, { name: string; pmsType?: string }>,
    [roomCategoriesResponse?.data],
  );
  const perPage =
    viewMode === ViewModes.KANBAN ? KANBAN_PER_PAGE : LIST_PER_PAGE;

  const dateFilters =
    activeTab === RequestType.ROOM
      ? {
          dueDateTimeStart: dateRangeFilter?.from
            ? new Date(dateRangeFilter.from).toISOString()
            : undefined,
          dueDateTimeEnd: dateRangeFilter?.to
            ? new Date(dateRangeFilter.to).toISOString()
            : undefined,
        }
      : {
          startDate: dateRangeFilter?.from
            ? new Date(dateRangeFilter.from).toISOString()
            : undefined,
          endDate: dateRangeFilter?.to
            ? new Date(dateRangeFilter.to).toISOString()
            : undefined,
        };

  const { data: fetchedRequests } = useRequests(
    {
      type: activeTab,
      page: currentPage,
      limit: perPage,
      bookingId: searchParams.get(REQUEST_SEARCH_PARAM_KEYS.booking),
      ...dateFilters,
      createdByIds: createdByFilter,
      assignedStaffIds: assignedStaffFilter,
      servicesIds: serviceFilter,
      priority: priorityFilter,
      status: statusFilter,
      sortField: sortField ?? "createdAt",
      sortDirection: sortDirection ?? "desc",
      hotelId,
    },
    !!hotelId,
  );

  const requests = useMemo(
    () => fetchedRequests?.data || [],
    [fetchedRequests],
  );

  const totalPages = Math.ceil(
    (fetchedRequests?.total || 0) / (fetchedRequests?.limit || 10),
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEditModal = (
    request: Request,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    setSelectedRequest(request);
    setIsEditModalOpen(true);
  };

  const handleUpdateRequest = async (
    requestId: string,
    updatedData: Partial<Request>,
  ) => {
    try {
      await updateRequestMutation.mutateAsync({
        id: requestId,
        data: updatedData,
      });
    } catch (error) {
      console.error(t("requests.page.failedToUpdateRequest"), error);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchParamsKey]);

  const updateSearchParams = (updater: (params: URLSearchParams) => void) => {
    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);

      updater(nextParams);

      return nextParams;
    });
  };

  const handleTabChange = (value: string) => {
    updateSearchParams((nextParams) => {
      nextParams.set(REQUEST_SEARCH_PARAM_KEYS.type, value);
    });
  };

  const handleViewModeChange = (value: string) => {
    const nextViewMode = getViewModeFromSearchParam(value);

    updateSearchParams((nextParams) => {
      nextParams.set(REQUEST_SEARCH_PARAM_KEYS.view, nextViewMode);
    });
  };

  const handleMultiFilterChange = (key: string) => (values: string[]) => {
    updateSearchParams((nextParams) => {
      setMultiValueSearchParam(nextParams, key, values);
    });
  };

  const handlePriorityFilterChange = (value: boolean) => {
    updateSearchParams((nextParams) => {
      if (value) {
        nextParams.set(REQUEST_SEARCH_PARAM_KEYS.priority, "true");
        return;
      }

      nextParams.delete(REQUEST_SEARCH_PARAM_KEYS.priority);
    });
  };

  const handleDateRangeChange = (value: DateRange) => {
    updateSearchParams((nextParams) => {
      const isDefaultRange =
        isSameDate(value.from, defaultDateRange.from) &&
        isSameDate(value.to, defaultDateRange.to);

      if ((!value.from && !value.to) || isDefaultRange) {
        nextParams.delete(REQUEST_SEARCH_PARAM_KEYS.startDate);
        nextParams.delete(REQUEST_SEARCH_PARAM_KEYS.endDate);
        return;
      }

      const startDate = formatDateSearchParam(value.from);
      const endDate = formatDateSearchParam(value.to);

      if (startDate) {
        nextParams.set(REQUEST_SEARCH_PARAM_KEYS.startDate, startDate);
      } else {
        nextParams.delete(REQUEST_SEARCH_PARAM_KEYS.startDate);
      }

      if (endDate) {
        nextParams.set(REQUEST_SEARCH_PARAM_KEYS.endDate, endDate);
      } else {
        nextParams.delete(REQUEST_SEARCH_PARAM_KEYS.endDate);
      }
    });
  };

  const tabsListClassName =
    "h-9 items-stretch gap-1 bg-secondary-badge p-1 text-black shadow-xs";
  const tabsTriggerClassName =
    "h-full rounded-md border border-transparent px-3 py-1 text-xs font-medium shadow-none data-[state=active]:border-custom-outline data-[state=active]:bg-royal-glow-5 data-[state=active]:text-black data-[state=active]:shadow-none";

  const selection = useCheckboxSelection({
    data: requests,
    getId: (request) => String(request.id),
  });

  const selectedTasks = useMemo(() => {
    const selectedRequests =
      requests.filter((request) =>
        selection.selectedItems.includes(String(request.id)),
      ) || [];
    return (
      selectedRequests
        ?.map((request) => request?.tasks?.[0]?.id)
        .filter((t) => t !== undefined) || []
    );
  }, [selection.selectedItems, requests]);

  return (
    <div className="flex flex-col gap-4 overflow-scroll p-5">
      <RequestStats
        dateRange={dateRangeFilter}
        isKanban={viewMode === ViewModes.KANBAN}
      />

      <div className="flex flex-col gap-4">
        <div className="flex h-full flex-row items-center justify-between">
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            <TabsList className={tabsListClassName}>
              {Object.values(RequestType).map((type) => (
                <TabsTrigger
                  key={type}
                  className={tabsTriggerClassName}
                  value={type}
                >
                  {getRequestTabLabel(type)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex items-center justify-end gap-2">
            <Tabs
              value={viewMode}
              onValueChange={handleViewModeChange}
              className="w-full"
            >
              <TabsList className={tabsListClassName}>
                {Object.values(ViewModes).map((mode) => (
                  <TabsTrigger
                    key={mode}
                    className={tabsTriggerClassName}
                    value={mode}
                  >
                    {ViewModeIcon[mode]("size-4")}
                    {getViewModeLabel(mode)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <CreateRequestModal
              requestType={RequestType.GUEST}
              triggerNode={
                <Button variant="new_default">
                  <PlusCircle />
                  {t("requests.page.guestRequest")}
                </Button>
              }
            />
            <CreateRequestModal
              requestType={RequestType.INTERNAL}
              triggerNode={
                <Button variant="new_outline">
                  <PlusCircle />
                  {t("requests.page.internalRequest")}
                </Button>
              }
            />
          </div>
        </div>
        <RequestFilters
          activeTab={activeTab}
          createdByFilter={createdByFilter}
          assignedStaffFilter={assignedStaffFilter}
          dateRangeFilter={dateRangeFilter}
          statusFilter={statusFilter}
          serviceFilter={serviceFilter}
          priorityFilter={priorityFilter}
          onStatusChange={handleMultiFilterChange(
            REQUEST_SEARCH_PARAM_KEYS.status,
          )}
          onServiceChange={handleMultiFilterChange(
            REQUEST_SEARCH_PARAM_KEYS.service,
          )}
          onPriorityChange={handlePriorityFilterChange}
          onDateRangeChange={handleDateRangeChange}
          onCreatedByChange={handleMultiFilterChange(
            REQUEST_SEARCH_PARAM_KEYS.createdBy,
          )}
          onAssignedStaffChange={handleMultiFilterChange(
            REQUEST_SEARCH_PARAM_KEYS.assignedStaff,
          )}
        />

        {selection?.selectedCount > 0 && (
          <RequestBulkActions
            selectedItems={selection?.selectedItems || []}
            selectedTasks={selectedTasks}
          />
        )}

        <RequestsView
          activeTab={activeTab}
          viewMode={viewMode}
          requests={requests}
          sortField={sortField}
          sortDirection={sortDirection}
          currentPage={currentPage}
          totalPages={totalPages}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onUpdateRequest={handleUpdateRequest}
          onEditRequest={handleEditModal}
          selection={selection}
          roomCategoryById={roomCategoryById}
        />
      </div>
      <EditRequestModal
        isModalOpen={isEditModalOpen}
        setIsModalOpen={setIsEditModalOpen}
        selectedRequest={selectedRequest}
        setSelectedRequest={setSelectedRequest}
      />
    </div>
  );
};
